/**
 * InviteLinkModal — persistent dialog that surfaces the raw invitation
 * URL after a Resend or Get-Link action.
 *
 * Context (founder problem, 2026-05-18):
 *   "i invited a user and it says email sent — the user never got the
 *    invitation and i am stuck now — user cant login? all the options
 *    are greyed out. i cant reset password or get his link and also
 *    email is not working. we need emails to work but when email is
 *    down we still need to be able to resend invite, or see the invite
 *    link and things like that"
 *
 * The previous UX surfaced the invite URL via a sonner toast with a
 * "Copy link" action. Toasts auto-dismiss after ~12s and the raw URL
 * cannot be re-retrieved once it's gone (the row only stores the hash).
 * If the admin misclicked Copy, dismissed the toast, or context-switched
 * before copying, they were stranded with no recovery path.
 *
 * This component fixes that gap by:
 *   1. Rendering a persistent modal that does NOT auto-dismiss. The
 *      admin closes it explicitly when they're done.
 *   2. Showing the email-delivery status banner at the top so the admin
 *      knows whether to ALSO share the link manually (failed / disabled)
 *      or whether the link is a backup channel (sent).
 *   3. Letting the admin copy the URL as many times as they want.
 *   4. Optionally offering a "Send via email again" button so the admin
 *      can retry SMTP from the same modal once it's configured.
 *
 * Companion gap: the user detail page (/users/:id) previously disabled
 * every action button when status='invited' with a tooltip pointing at
 * the Invitations tab. The W1a-08 "Invited User Recovery" action group
 * now opens this modal directly from the detail page.
 */

import { useState } from 'react';
import { Copy, Mail, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * Status of the most recent email-delivery attempt.
 *
 *   - `sent`     — SMTP successfully accepted the message. The link in
 *                  the modal is a backup channel; the user should also
 *                  receive the email.
 *   - `failed`   — SMTP threw or rejected. The admin MUST share the link
 *                  manually; no email arrived.
 *   - `disabled` — SMTP is not configured for this deployment. The link
 *                  is the ONLY delivery channel — share manually.
 */
export type EmailDeliveryStatus = 'sent' | 'failed' | 'disabled';

export interface InviteLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Full URL (origin + path + token) the admin can copy. */
  inviteUrl: string;
  /** ISO-8601 expiry timestamp from the API. */
  expiresAt: string;
  /** Result of the most recent email-send attempt. */
  emailDelivery: EmailDeliveryStatus;
  /** Sanitized SMTP error string (only when emailDelivery === 'failed'). */
  emailError?: string;
  /**
   * If provided + emailDelivery !== 'disabled', shows a "Send via email
   * again" button that calls this callback. The modal stays open so the
   * admin can also share the link manually if the retry fails too.
   */
  onResend?: () => void;
  /** Email of the invitee — surfaced in the banner text. */
  recipientEmail?: string;
}

/**
 * Banner styling per email-delivery state. We use `Info` (blue) for the
 * not-configured case rather than `AlertTriangle` (amber) because the
 * not-configured state is a documented deployment mode (air-gap), not a
 * failure the admin needs to escalate.
 */
function getBannerProps(status: EmailDeliveryStatus): {
  Icon: typeof CheckCircle2;
  className: string;
  title: string;
  body: (recipientEmail?: string, error?: string) => string;
} {
  switch (status) {
    case 'sent':
      return {
        Icon: CheckCircle2,
        className:
          'bg-emerald-500/10 border-emerald-500/40 text-emerald-800 dark:text-emerald-300',
        title: 'Invitation email sent',
        body: (recipientEmail) =>
          recipientEmail
            ? `${recipientEmail} should receive the invitation shortly. You can also share the link below as a backup.`
            : 'The invitation email was queued for delivery. You can also share the link below as a backup.',
      };
    case 'failed':
      return {
        Icon: AlertTriangle,
        className:
          'bg-amber-500/10 border-amber-500/40 text-amber-900 dark:text-amber-200',
        title: 'Email delivery failed — share this link manually',
        body: (recipientEmail, error) => {
          const who = recipientEmail ? `to ${recipientEmail}` : '';
          const why = error ? ` Reason: ${error}` : '';
          return `The invitation row was created and the link below is valid, but the email could not be delivered ${who}. Copy the link and send it through another channel.${why}`;
        },
      };
    case 'disabled':
      return {
        Icon: Info,
        className:
          'bg-sky-500/10 border-sky-500/40 text-sky-900 dark:text-sky-200',
        title: 'Email delivery is not configured',
        body: (recipientEmail) =>
          recipientEmail
            ? `SMTP is not configured for this deployment, so no email was sent to ${recipientEmail}. Share the link below manually.`
            : 'SMTP is not configured for this deployment. Share the link below manually.',
      };
  }
}

/**
 * Render an ISO date as "expires in N days" / "expires today" / a
 * concrete date if more than 30 days out. Mirrors the formatting on
 * the Invitations tab so admins see a consistent expiry hint.
 */
function formatExpiry(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return `expires ${expiresAt}`;
  const days = Math.ceil(ms / 86_400_000);
  if (days <= 0) return 'expires today';
  if (days === 1) return 'expires in 1 day';
  if (days <= 30) return `expires in ${days} days`;
  return `expires ${new Date(expiresAt).toLocaleDateString()}`;
}

export function InviteLinkModal({
  open,
  onOpenChange,
  inviteUrl,
  expiresAt,
  emailDelivery,
  emailError,
  onResend,
  recipientEmail,
}: InviteLinkModalProps) {
  const [copied, setCopied] = useState(false);
  const banner = getBannerProps(emailDelivery);
  const BannerIcon = banner.Icon;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success('Invitation link copied');
      // Reset the "Copied" state after 2s so the admin can copy again
      // without losing the affordance. We deliberately do NOT close the
      // modal on copy — admins frequently want the URL multiple times
      // (paste into chat + email + ticket system).
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl"
        // Persistent on outside-click — admin must explicitly close.
        // The Esc key + the built-in close (X) button + the Close
        // footer button all close normally; only the soft "click
        // outside" path is hardened.
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Invitation link
          </DialogTitle>
          <DialogDescription>
            Share this link with the invitee. They land on the accept-invite
            page where they set their password and complete onboarding.
          </DialogDescription>
        </DialogHeader>

        {/* Email-delivery status banner. Always present so the admin
            knows whether to expect email delivery or has to share
            manually. */}
        <div
          className={cn(
            'rounded-md border px-3 py-2.5 text-xs flex items-start gap-2',
            banner.className,
          )}
          role="status"
          aria-live="polite"
        >
          <BannerIcon className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <div className="font-semibold">{banner.title}</div>
            <div>{banner.body(recipientEmail, emailError)}</div>
          </div>
        </div>

        {/* URL + Copy button. The input is read-only + auto-selected on
            focus so the admin can copy via keyboard if the Copy button
            fails (older browsers, clipboard permission denied). */}
        <div className="space-y-1.5">
          <Label htmlFor="invite-url" className="text-xs">
            Invitation URL <span className="text-muted-foreground">({formatExpiry(expiresAt)})</span>
          </Label>
          <div className="flex gap-1.5">
            <Input
              id="invite-url"
              value={inviteUrl}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              className="text-xs font-mono"
              data-testid="invite-link-input"
            />
            <Button
              type="button"
              size="sm"
              variant={copied ? 'default' : 'outline'}
              onClick={handleCopy}
              className="shrink-0"
              data-testid="invite-link-copy-btn"
              title="Copy invitation link to clipboard"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5 mr-1.5" />
                  Copy
                </>
              )}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            The link is valid until expiry or until the invitation is
            revoked. Generating a new link automatically invalidates this
            one.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {onResend && emailDelivery !== 'disabled' && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onResend}
              title="Try sending the invitation email again"
            >
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              Send via email again
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={() => onOpenChange(false)}
            data-testid="invite-link-close-btn"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
